import type { InvoiceDirection } from '../api/use-applied-provisional';
import { usePricingReadiness } from '../api/use-pricing-readiness';
import { useQpWindow } from '../api/use-qp-window';

export interface QpDialogWarningProps {
  readonly blOrderId: string;
  readonly side: 'buy' | 'sell';
  // Final settlement suppresses the warning once the side's price is fixed
  // (Launchpad's `qpStatus.concluded || priceFixed`). Provisional never does.
  readonly suppressWhenPriceFixed?: boolean;
}

// "QP not concluded" warning inside the Provisional / Final create dialogs
// (Launchpad parity). Launchpad deliberately renders this date-less — only the
// anchor formula — so it stays informational and never blocks the save. Reads
// the BL-level QP window (sell ticket preferred); both queries are already
// fetched by the dialog body, so this is a cache hit.
export function QpDialogWarning({
  blOrderId,
  side,
  suppressWhenPriceFixed = false,
}: QpDialogWarningProps) {
  const direction: InvoiceDirection = side === 'buy' ? 'Purchase' : 'Sale';
  const { data: qp } = useQpWindow(blOrderId);
  const { data: readiness } = usePricingReadiness(blOrderId, direction);
  if (qp?.has_qp !== true) return null;
  if (suppressWhenPriceFixed && (readiness?.price_fixed ?? false)) return null;
  return (
    <div
      className="border-warning/40 bg-warning/10 rounded-md border p-3 text-sm"
      role="alert"
      data-testid="qp-dialog-warning"
    >
      <p className="font-medium">QP not concluded</p>
      <p className="text-muted-foreground mt-1 text-xs">
        The averaging period
        {qp.formula_label !== null ? ` (${qp.formula_label})` : ''} has not ended yet. The final LME
        settlement price is not yet known.
      </p>
    </div>
  );
}
